/**
 * Admin V3 — Overview module — KPI grid (Row 1)
 *
 * 4 KPI cards using a simplified local KpiCard (no sparkline). Class strings
 * mirror legacy AdminDashboard.js:170-214 (white card, rounded-[22px],
 * slate-200 border, soft shadow) for visual continuity.
 *
 * Silent fallback to "—" on error per owner directive (Phase 2.1 decision #5).
 */

import React from 'react';
import { Euro, Calendar, Car, Clock3 } from 'lucide-react';
import { cn, currency } from '../../shared/visualTokens';
import { t } from '../../i18n';

// Simplified KPI card (no sparkline). Class strings verbatim from legacy.
const KpiCard = ({ title, value, icon: Icon, iconTone, loading }) => (
  <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition hover:-translate-y-[1px] hover:shadow-[0_12px_36px_rgba(15,23,42,0.08)]">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', iconTone)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-medium text-slate-500">{title}</div>
          <div className="mt-1 flex items-end gap-2">
            <div className="text-[18px] font-bold tracking-tight text-slate-900 md:text-[20px]">
              {loading ? '...' : value}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const OverviewKpiGrid = ({ dashboard, demand, locale = 'fr' }) => {
  // Silent fallback to "—" on error; show "..." while loading; otherwise the value.
  const fmt = (slice, accessor) => {
    if (slice.loading) return null; // signals loading state via KpiCard.loading
    if (slice.error || !slice.data) return '—';
    try {
      const v = accessor(slice.data);
      return v === null || v === undefined ? '—' : v;
    } catch {
      return '—';
    }
  };

  const revenueToday = fmt(dashboard, (d) => currency(d?.revenue?.today));
  const bookingsToday = fmt(dashboard, (d) => d?.bookings?.today);
  const driversOnline = fmt(dashboard, (d) => d?.drivers?.online);
  const pendingRequests = fmt(demand, (d) => d?.pending_requests);

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        title={t('overview.kpi.revenueToday', locale)}
        value={revenueToday}
        loading={dashboard.loading}
        icon={Euro}
        iconTone="bg-slate-100 text-slate-700"
      />
      <KpiCard
        title={t('overview.kpi.bookingsToday', locale)}
        value={bookingsToday}
        loading={dashboard.loading}
        icon={Calendar}
        iconTone="bg-blue-100 text-blue-600"
      />
      <KpiCard
        title={t('overview.kpi.driversOnline', locale)}
        value={driversOnline}
        loading={dashboard.loading}
        icon={Car}
        iconTone="bg-emerald-100 text-emerald-600"
      />
      <KpiCard
        title={t('overview.kpi.pendingRequests', locale)}
        value={pendingRequests}
        loading={demand.loading}
        icon={Clock3}
        iconTone="bg-orange-100 text-orange-600"
      />
    </div>
  );
};

export default OverviewKpiGrid;
